import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  LoadPanel,
  Lookup,
  Column,
  HeaderFilter,
  MasterDetail,
  Editing,
  Scrolling,
  Toolbar,
  SearchPanel,
  Popup,
  Item,
  Button as DataButton,
} from "devextreme-react/data-grid";
import Alert from "@mui/material/Alert";
import { Button } from "devextreme-react/button";
import CheckBox from "devextreme-react/check-box";
import LoadingSpinnerModal from "../../Components/LoadingSpinnerModal";
import { apiBaseURL } from "../../Utils/ProjectConstants";
import useFetch from "../../Hooks/useFetch";
import { isMobile } from "../../Utils/media-query";
import {
  getUsersWorkCenter,
  getUsersName,
  checkRoles,
} from "../../Utils/helper";
import ActionNotifications from "../../Components/ActionNotifications";
import ErrorMessage from "../../Components/ErrorMessage";
import DetailTemplate from "./Components/detailTemplate.js";
import OrderForm from "./Components/orderForm";
import ProvideAssistance from "../../Components/ProvideAssistance/provideAssistance";
import RequestAssistance from "../../Components/RequestAssistance/requestAssistance";
import useGetPaginatedOrders from "../../Hooks/useGET/useGetPaginatedOrders";
import SelectBox from "devextreme-react/select-box";
import useSearchOrders from "../../Hooks/useGET/useSearchOrders";
import { renderStatus, renderUrgency } from "./Components/renderConst";
import "./styles.css";
/*
-=-=-=-=-=-=-=-=-=-=-=-= Orders Page -=-=-=-=-=-=-=-=-=-=-=-=
=-=-=-=-=-=-=-= https://atlasboard.wsu-idp.org/orders =-=-=-=-=-=-=-
*/

const statuses = [
    { value: 0, text: "New" },
    { value: 1, text: "Supervisor Approved" },
    { value: 2, text: "PCard Approved" },
    { value: 3, text: "On Hold" },
    { value: 4, text: "Assistance Requested" },
    { value: 5, text: "Assistance Provided" },
    { value: 6, text: "Ordered" },
    { value: 7, text: "Ordered" }, // Shipped ->removed this step -> named as Ordered
    { value: 8, text: "Ordered" }, // Out for Delivery-> removed this step -> named as Ordered
    { value: 9, text: "Ordered" }, // Delivered-> removed this step -> named as Ordered
    { value: 10, text: "Denied" },
    { value: 12, text: "Cancelled" },
    { value: 12, text: "Out of Stock" },
    { value: 13, text: "Order Edited" },
    { value: 14, text: "Order Processing" },
  ],
  urgencyDataSource = [
    { value: 0, text: "High" },
    { value: 1, text: "Standard" },
    { value: 2, text: "Low" },
  ];

/*-=-=-=-=-=-=-=-=-=-=-=-= Orders Page -=-=-=-=-=-=-=-=-=-=-=-=*/
export default function OrdersPage() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [inputText, setInputText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [orderView, setOrderView] = useState(false);
  const {
    orders: searchedOrders,
    isLoading: isSearchLoading,
    noResults,
  } = useSearchOrders(searchTerm, orderView);
  const workcenter = getUsersWorkCenter()?.name || "",
    {
      orders,
      mutate,
      error,
      isLoading,
      noResults: noResultsPag,
    } = useGetPaginatedOrders(pageIndex, pageSize, orderView),
    filteredOrders = orders?.filter(
      (x) => x.orderHandleBy?.name === getUsersName(),
    ),
    [filtered, setFiltered] = useState(false),
    dataSource = searchTerm
      ? searchedOrders
      : filtered
        ? filteredOrders
        : orders,
    navigate = useNavigate(),
    fetch = useFetch(navigate),
    [notifyResponse, setResponse] = useState({});
  useEffect(() => {
    console.log("notifyResponse", notifyResponse);
  }, [notifyResponse]);

  const [showPopup, setPopup] = useState({
    provideAssistance: null,
    requestAssistance: null,
    orderForm: null,
    // pcards: null,
    // deliveryLocations: null,
  });

  const fetchSuccessful = (response) => {
    setResponse(response);
    return (
      response?.success &&
      response?.data?.statusCode &&
      response?.success !== "Invalid Model"
    );
  };

  const handlePopup = useCallback((popupKey, data) => {
    console.log("handlePopup data", data);
    setPopup((prevState) => ({
      ...prevState,
      [popupKey]: { data },
    }));
  }, []);

  const handleClose = (popupKey) => {
    setPopup((prevState) => ({
      ...prevState,
      [popupKey]: null,
    }));
  };

  const onRowRemoved = async (e) => {
    const response = await fetch.delete(`${apiBaseURL}/api/Orders?id=${e.key}`);
    fetchSuccessful(response);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(inputText.trim());
    }, 900); // Debounce time in ms

    return () => clearTimeout(timeoutId);
  }, [inputText]);

  const renderGrid = () => {
    const onValueChanged = (e) => {
      setFiltered(!filtered);
    };

    return (
      <>
        <DataGrid
          dataSource={dataSource}
          keyExpr="id"
          rowAlternationEnabled={false}
          showBorders={true}
          height="calc(100vh - 220px)"
          width="100%"
          allowColumnResizing={true}
          repaintChangesOnly={true}
          columnHidingEnabled={isMobile}
          onEditorPreparing={onEditorPreparing}
          onRowRemoved={onRowRemoved}
          onRowPrepared={onRowPrepared}
          wordWrapEnabled={true}
          onOptionChanged={(e) => {
            if (e.fullName === "searchPanel.text") {
              setSearchTerm(e.value); //Triggers api
            }
          }}
        >
          <Scrolling mode="virtual" />
          <HeaderFilter visible={true} />

          {/*TopBar for table grid*/}

          <SearchPanel
            visible={true}
            maxWidth={275}
            searchVisibleColumnsOnly={true}
            placeholder="Search..."
          />
          <Toolbar>
            <Item
              location="before"
              locateInMenu="never"
              render={() => (
                <div>
                  <h1 className="white">Orders</h1>
                </div>
              )}
            />
            <Item locateInMenu="auto">
              <CheckBox
                className="orders-checkbox"
                value={filtered}
                text="My Orders"
                visible={workcenter === "Operations"}
                onValueChanged={onValueChanged}
              />
            </Item>
            <Item locateInMenu="auto">
              <Button
                text={orderView ? "Show All Orders" : "Show Ready to Order"}
                type="normal"
                stylingMode="contained"
                visible={workcenter === "Operations"}
                onClick={() => setOrderView(!orderView)}
              />
            </Item>

            <Item>
              <Button
                text={isMobile ? "" : "New Order"}
                type="normal"
                stylingMode="contained"
                icon="add"
                onClick={() => handlePopup("orderForm", {})}
              />
            </Item>
            <Item name="addRowButton" />
            <Item name="searchPanel" />
          </Toolbar>

          <Editing allowDeleting mode="popup">
            <Popup
              title="Order Info"
              fullScreen={isMobile}
              showTitle={true}
              width="50%"
              height="80%"
            />
          </Editing>

          {/* Column Selection */}
          <Column
            type="buttons"
            name="Actions"
            hidingPriority={5}
            width={isMobile ? "0%" : 150}
          >
            <DataButton
              text="Provide Assistance"
              icon="toolbox"
              visible={isProvideButtonVisible}
              onClick={(params) =>
                handlePopup("provideAssistance", params.row.data.id)
              }
            />
            <DataButton
              text="Request Assistance"
              icon="help"
              visible={workcenter === "Operations" && isRequestButtonVisible}
              onClick={(params) =>
                handlePopup("requestAssistance", params.row.data.id)
              }
            />
            <DataButton
              text="Order Details"
              icon="info"
              onClick={(params) => navigate(`/orders/${params.row.data.id}`)}
            />
            <DataButton
              text="Edit"
              visible={true} // Ensure this is set to true
              icon="edit"
              onClick={(e) => handlePopup("orderForm", e.row.data)}
            />
            <DataButton name="delete" />
          </Column>
          <Column
            dataField="id"
            hidingPriority={9}
            defaultSortOrder="desc"
            allowHeaderFiltering={false}
            width={75}
          />
          <Column
            dataField="orderedBy.name"
            caption="Ordered By"
            hidingPriority={9}
          />
          <Column
            dataField="orderedDate"
            dataType="datetime"
            hidingPriority={6}
          />
          <Column
            dataField="requestorContactNumber"
            //visible={getUsersWorkCenter() === "Operations"}
            hidingPriority={3}
          />
          <Column
            dataField="orderHandledBy.name"
            caption="Order Handled By"
            hidingPriority={4}
          />
          <Column
            dataField="orderHandled"
            dataType="datetime"
            cellRender={handledDateRender}
            hidingPriority={1}
          />
          <Column
            dataField="pcardHolder.name"
            caption="PCard Holder"
            hidingPriority={2}
          />
          <Column
            dataField="urgency"
            caption="Urgency"
            cellRender={(e) => renderUrgency(e.data.urgency)}
            hidingPriority={7}
            width={100}
          >
            <Lookup
              dataSource={urgencyDataSource}
              valueExpr="value"
              displayExpr="text"
            />
          </Column>
          <Column
            dataField="status"
            caption="Status"
            cellRender={(e) => renderStatus(e.data.status)}
            hidingPriority={8}
          >
            <Lookup
              dataSource={statuses}
              valueExpr="value"
              displayExpr="text"
            />
            <HeaderFilter dataSource={statuses} groupInterval={1} />
          </Column>

          {dataSource?.vendors?.map((vendor, index) => (
            <>
              <Column
                dataField={`vendors[${index}].name`}
                visible={false}
                caption="Name"
              />
              <Column
                dataField={`vendors[${index}].trackingNumber`}
                visible={false}
                caption="Tracking Number"
              />
              {vendor.items?.map((item, itemIndex) => (
                <>
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].itemName`}
                    visible={false}
                    caption={`Item ${index + 1}`}
                  />
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].itemNumber`}
                    visible={false}
                    caption="Item Number"
                  />
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].deliveryLocationId`}
                    visible={false}
                    caption="Delivery Location"
                  />
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].quantity`}
                    visible={false}
                    caption="Quantity"
                  />
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].pricePerItem`}
                    visible={false}
                    caption="Price per Item"
                  />
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].url`}
                    visible={false}
                    caption="URL"
                  />
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].billingCode`}
                    visible={false}
                    caption="Billing Code"
                  />
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].notes`}
                    visible={false}
                    caption="Notes"
                  />
                  <Column
                    dataField={`vendors[${index}].items[${itemIndex}].isOutOfStock`}
                    visible={false}
                    caption="Out of Stock"
                  />
                </>
              ))}
            </>
          ))}
          <MasterDetail
            enabled={true}
            component={(detailInfo) => (
              <DetailTemplate
                orderData={detailInfo.data.data}
                setNotifications={setResponse}
                mutate={mutate}
              />
            )}
          />
        </DataGrid>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderTop: "1px solid #eee",
            backgroundColor: "transparent",
            marginTop: 16,
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ fontWeight: 500, color: "white" }}>
              Orders Per Page:
            </span>
            <SelectBox
              items={[10, 25, 50, 100, 500]}
              value={pageSize}
              onValueChanged={(e) => {
                setPageSize(e.value);
                setPageIndex(0);
                localStorage.setItem("pageSize", e.value);
              }}
              width={100}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Button
              text="← Prev"
              disabled={pageIndex === 0}
              onClick={() => setPageIndex((p) => p - 1)}
            />
            <span style={{ fontWeight: 500, color: "white" }}>
              Page {pageIndex + 1}
            </span>
            <Button text="Next →" onClick={() => setPageIndex((p) => p + 1)} />
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div className="px-lg-4 page-content">
        <ActionNotifications response={notifyResponse} />
        {showPopup.provideAssistance && (
          <ProvideAssistance
            orderId={showPopup.provideAssistance.data}
            setNotifications={setResponse}
            onClose={() => handleClose("provideAssistance")}
          />
        )}
        {showPopup.requestAssistance && (
          <RequestAssistance
            orderId={showPopup.requestAssistance.data}
            setNotifications={setResponse}
            onClose={() => handleClose("requestAssistance")}
          />
        )}
        {showPopup.orderForm && (
          <OrderForm
            setNotifications={setResponse}
            mutate={mutate}
            selectedOrder={showPopup.orderForm.data}
            onClose={() => handleClose("orderForm")}
          />
        )}
        {/* {showPopup.pcards && (
          <PCards
            fetchSuccessful={fetchSuccessful}
            onClose={() => handleClose("pcards")}
          />
        )}
        {showPopup.deliveryLocations && (
          <DeliveryLocations
            setNotifications={setResponse}
            onClose={() => handleClose("deliveryLocations")}
          />
        )} */}
        <div className="orders-datagrid contentBox">
          {noResults || noResultsPag ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "calc(100vh - 120px)", // Adjust this if your header is taller
                padding: "20px",
              }}
            >
              <div className="no-results-box">
                <h2
                  style={{
                    marginBottom: 16,
                    fontWeight: 600,
                    fontSize: "1.25rem",
                  }}
                >
                  {noResults
                    ? `No results found for "${searchTerm}".`
                    : "No results found."}
                </h2>
                <Button
                  text="← Back"
                  type="normal"
                  stylingMode="contained"
                  onClick={() => {
                    if (noResults) {
                      setSearchTerm("");
                      setInputText("");
                    } else {
                      setPageIndex(pageIndex - 1);
                      setPageSize(500);
                    }
                  }}
                />
              </div>
            </div>
          ) : !error ? (
            <>
              <LoadingSpinnerModal show={isLoading || isSearchLoading} />
              {renderGrid()}
            </>
          ) : (
            <ErrorMessage />
          )}
        </div>
      </div>
    </>
  );
}
const onEditorPreparing = (e) => {
  if (
    (e.parentType === "dataRow" || e.parentType === "filterRow") &&
    e.dataField === "tasks"
  ) {
    e.editorName = "dxTagBox";
    e.editorOptions.showSelectionControls = true;
    e.editorOptions.displayExpr = "name";
    e.editorOptions.valueExpr = "id";
    e.editorOptions.value = e.value || [];
    e.editorOptions.onValueChanged = (x) => {
      //replace?
      console.log(x);
      e.setValue(x.value);
    };
  }
};

const handledDateRender = (e) => {
  if (e.data.orderHandled === "0001-01-01T00:00:00") {
    return "N/A";
  } else {
    return e.text;
  }
};

const onRowPrepared = (e) => {
  if (e.rowType === "header") {
    e.rowElement.style.backgroundColor = "#FFCD00";
    e.rowElement.style.fontWeight = "bold";
  }
};

const isRequestButtonVisible = (e) => {
  if (
    (e.row.data.status >= 2 &&
      e.row.data.status <= 5 &&
      e.row.data.status !== 4) ||
    e.row.data.status === 15
  ) {
    return true;
  }
};
const isProvideButtonVisible = (e) => {
  if (e.row.data.status === 4) {
    return true;
  }
};
